import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import {
    getAssetsToSellTotalForDrawdown,
    getAssetsToSellTotalForUFPLS,
    ShouldDisplayItfcChargesMessage,
} from "../../../Common/SIPP/DrawdownDetails/Helper/DrawdownDetailHelper";
import { SectionHeader } from "../../../Common/SharedComponents";
import { ChooseAssetsToSellFor } from "../../../Common/SIPP/DrawdownDetails/Resource/Contents";
import { MoneyHelper } from "../../../../../../Shared/Common/MoneyHelper";
import { getTotalIncome } from "../../../Common/SIPP/IncomePaymentDetails/Helper/IncomePaymentHelper";
import { ChooseAssetsToSellAssets } from "../../../Common/SIPP/DrawdownDetails/Store/DrawdownCalculationStoreStatic";
import { InvestmentAllocation } from "../../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { processTypeEnum } from "../../../Common/Resource/Contents";
import { PensionOption } from "../../../../../../../Wrap/Constants/Enums/Apply/PensionOption";
import { ProductType } from "../../../../../../../Wrap/Constants/Enums/Domain/ProductType";
import { PensionContribution } from "../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContribution";
import { TableKeys } from "../../../Common/SIPP/DrawdownDetails/Resource/Constant";

interface IChooseAssetsToSellProps extends IProcessComponentProps {
    data: DrawdownModel;
    tableKey: string;
    sectionHeader?: string;
    processType?: processTypeEnum;
    reason?: ChooseAssetsToSellFor;
    accountHasETIHoldings?: Boolean;
    assets: ChooseAssetsToSellAssets[];
    disableOffPlatform?: boolean;
    pensionBase?: PensionContribution;
    isAcrossAllAssets: boolean;
    setIsAcrossAllAssets: (isAcrossAll: boolean) => void;
}

interface IChooseAssetsToSellState {
    shouldCalculateProportionalAllocation: boolean;
}

export class ChooseAssetsToSell extends ProcessComponent<IChooseAssetsToSellProps, IChooseAssetsToSellState> {
    constructor(props: any) {
        super(props);
        this.state = {
            shouldCalculateProportionalAllocation: props.data?.DrawdownType !== DrawdownType.Regular && props.reason != ChooseAssetsToSellFor.Income && props.tableKey != TableKeys.PensionCommencementLumpSumAllocations,
        };
    }

    componentDidMount() {
        super.componentDidMount();
    }

    private shouldDisplayDisinvestmentMoney() {
        const { data, reason, sectionHeader } = this.props;
        return (!sectionHeader || sectionHeader.trim() === "") && reason === ChooseAssetsToSellFor.Income && data?.DrawdownType !== DrawdownType.Regular

    }

    getIsAcrossAll() {
        return this.props.isAcrossAllAssets;
    }

    public render(): JSX.Element {
        const { data, onChange, sectionHeader, processType, reason } = this.props;
        const shouldDisplayItfcChargesMessage = ShouldDisplayItfcChargesMessage(processType, reason);
        
        return (
            <>
                <hr />
                {SectionHeader(sectionHeader ? sectionHeader : "Choose assets to sell")}
                {this.shouldDisplayDisinvestmentMoney() && (
                    <div className="row mb-10">
                        <span className="col-auto ml-3">
                            Disinvestment to pay income ({MoneyDisplayHelper.FromatMoneyDisplay(
                                getTotalIncome(data,this.props?.pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction),
                                MoneyHelper.getDefaultCurrency()
                            )})
                        </span>
                    </div>)
                }
                {shouldDisplayItfcChargesMessage && (
                    <p>
                        {data?.DrawdownType !== DrawdownType.Regular && 
                         data?.PensionOption !== PensionOption.UFPLS 
                         ? (
                            <>{`Disinvestment to pay PCLS and charges (${MoneyDisplayHelper.getMoneyFormat({
                                Amount: getAssetsToSellTotalForDrawdown(data),
                                Currency: CurrencyCode.GBP,
                            })})`}</>
                        ) : data?.PensionOption === PensionOption.UFPLS ? (
                            <>{`Disinvestment to pay UFPLS (${MoneyDisplayHelper.getMoneyFormat({
                                Amount: getAssetsToSellTotalForUFPLS(data),
                                Currency: CurrencyCode.GBP,
                            })})`}</>
                        ) : (
                            `Disinvestment to pay drawdown and charges`
                        )}
                    </p>
                )}

                <RadiosEditor
                    options={[true, false]}
                    label={"Choose asset selection method"}
                    getText={(v) => (v ? "Proportionally across all assets" : "From specified assets")}
                    onChange={(v) => {
                        this.props.onChange({
                            IsAcrossAllAssets: v,
                            [this.props.tableKey]: v ? this.allocateAcrossAllAssets(this.props.assets) : []
                        });

                        this.props.setIsAcrossAllAssets(v);
                    }}
                    cssWrapper={"rg-inline mb-10"}
                    selected={ this.getIsAcrossAll()}
                />
            </>
        );
    }

    private allocateAcrossAllAssets(assets: ChooseAssetsToSellAssets[]): InvestmentAllocation[] {
        if(!this.state.shouldCalculateProportionalAllocation){
            return []; //wraptor responsibility to decide allocation
        }

        if (this.props.disableOffPlatform) {
            assets = assets.filter((asset) => asset.ProductType !== ProductType.OffPlatform);
        }

        const totalAmount = assets.reduce((total, asset) => total + asset.MarketValue.Amount, 0);
        let adjustedAssets = assets.map((asset) => ({
            Name: asset.Assets,
            Percentage: Math.floor((asset.MarketValue.Amount / totalAmount) * 10000) / 100,
            ProductId: asset.Id,
            Type: asset.ProductType,
        }));

        let totalPercentage = adjustedAssets.reduce((sum, asset) => sum + asset.Percentage * 100, 0) / 100;
        let remainder = (100 * 100 - totalPercentage * 100) / 100;

        if (adjustedAssets.length > 0) {
            for (let i = 0; i < Math.abs(remainder * 100); i++) {
                if (remainder > 0) {
                    adjustedAssets[i % adjustedAssets.length].Percentage = (adjustedAssets[i % adjustedAssets.length].Percentage * 100 + 1) / 100;
                } else {
                    adjustedAssets[i % adjustedAssets.length].Percentage = (adjustedAssets[i % adjustedAssets.length].Percentage * 100 - 1) / 100;
                }
            }
        }

        return adjustedAssets;
    }
}
